'use client';

import Link from "next/link";
import Image from "next/image";
import Script from "next/script";
import { useEffect, useRef, useState } from "react";
import { faInstagram, faFacebook, faYoutube } from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

declare global {
  interface Window {
    tiktokEmbed?: {
      lib: {
        render: () => void;
      };
    };
  }
}

export default function TikTokSection() {
  const [shouldLoadTikTok, setShouldLoadTikTok] = useState(false);
  const tiktokSectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    // Lazy load TikTok script only when section is about to be visible
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !shouldLoadTikTok) {
            setShouldLoadTikTok(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin: '200px' } // Start loading 200px before section is visible
    );

    if (tiktokSectionRef.current) {
      observer.observe(tiktokSectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [shouldLoadTikTok]);

  useEffect(() => {
    if (!shouldLoadTikTok) return;

    const addIframeTitles = () => {
      try {
        const iframes = document.querySelectorAll('.tiktok-embed iframe');
        iframes.forEach((iframe) => {
          if (!iframe.getAttribute('title')) {
            iframe.setAttribute('title', 'TikTok video embed');
          }
        });
      } catch (error) {
        console.debug('Error adding iframe titles:', error);
      }
    };

    const timer = setTimeout(() => {
      addIframeTitles();
    }, 1000);

    return () => {
      clearTimeout(timer);
    };
  }, [shouldLoadTikTok]);
  
  return (
    <section ref={tiktokSectionRef} className="relative">
      {/* TikTok embed script - Loaded only when TikTok section is about to be visible to avoid blocking critical path */}
      {shouldLoadTikTok && (
        <Script
          src="https://www.tiktok.com/embed.js"
          strategy="lazyOnload"
          onLoad={() => {
            try {
              if (typeof window !== 'undefined' && window.tiktokEmbed?.lib) {
                window.tiktokEmbed.lib.render();
                // Add titles after rendering
                setTimeout(() => {
                  const iframes = document.querySelectorAll('.tiktok-embed iframe');
                  iframes.forEach((iframe) => {
                    if (!iframe.getAttribute('title')) {
                      iframe.setAttribute('title', 'TikTok video embed');
                    }
                  });
                }, 500);
              }
            } catch (error) {
              // Silently handle TikTok embed errors
              console.debug('TikTok embed initialization:', error);
            }
          }}
          onError={() => {
            console.debug('TikTok embed script failed to load');
          }}
        />
      )}
      <div className="hidden xl:block absolute top-11 w-full inset-0 z-0">
        <div className="relative w-full h-full">
          <Image src="/images/decoration/Oval.png" alt="Decoration" fill className="object-cover" />
        </div>
      </div>
      <div className="relative flex flex-col items-center justify-center mx-auto py-8 px-8 bigscreen-responsive z-20 md:px-0">
        <p className="font-bold text-white bg-green-light rounded-bl-full rounded-tr-full w-70 mb-8 py-2 px-6 md:mb-4">Uníte  a nuestra comunidad</p>
        <div className="flex items-center justify-center py-8">
          <Image src="/icons/tikTok.svg" alt="TikTok" className="h-9 md:h-8 mr-2" width={36} height={36} />
          <p className="text-xl md:text-2xl">/@amuracr</p>
        </div>
        <div className="flex flex-col items-start justify-center text-center gap-8 md:flex-row md:justify-between md:text-left">
          <blockquote className="tiktok-embed" cite="https://www.tiktok.com/@amuracr/video/7335945152382373125" data-video-id="7335945152382373125" style={{maxWidth: "605px", minWidth: "325px"}}> 
            <section> 
              <Link target="_blank" title="@amuracr" href="https://www.tiktok.com/@amuracr?refer=embed">@amuracr</Link> La realidad que no todos ven 🧑‍🍳🧽😂 
              <Link title="humor" target="_blank" href="https://www.tiktok.com/tag/humor?refer=embed">#humor</Link> 
              <Link title="chefenproceso" target="_blank" href="https://www.tiktok.com/tag/chefenproceso?refer=embed">#chefenproceso</Link> 
              <Link title="amuracentroculinario" target="_blank" href="https://www.tiktok.com/tag/amuracentroculinario?refer=embed">#amuracentroculinario</Link> 
              <Link title="gastronomia" target="_blank" href="https://www.tiktok.com/tag/gastronomia?refer=embed">#gastronomia</Link> 
              <Link title="escueladecocina" target="_blank" href="https://www.tiktok.com/tag/escueladecocina?refer=embed">#escueladecocina</Link> 
              <Link target="_blank" title="♬ sonido original - mysweetbite_rm 🍩🎂🧁" href="https://www.tiktok.com/music/sonido-original-7287734692420930309?refer=embed">♬ sonido original - mysweetbite_rm 🍩🎂🧁</Link> 
            </section>
          </blockquote>
          <blockquote className="tiktok-embed" cite="https://www.tiktok.com/@amuracr/video/7193548455816613125" data-video-id="7193548455816613125" style={{maxWidth: "605px", minWidth: "325px"}}> 
            <section> 
              <Link target="_blank" title="@amuracr" href="https://www.tiktok.com/@amuracr?refer=embed">@amuracr</Link> El chef Marco y el chef Pablo, profes de Amura, mostraron fuera de las clases sus habilidades. ¿Quien creés que ganó?¿El chef pastelero o el chef cocinero?🤔🤩 
              <Link title="pasteleria" target="_blank" href="https://www.tiktok.com/tag/pasteleria?refer=embed">#pasteleria</Link> 
              <Link title="panaderia" target="_blank" href="https://www.tiktok.com/tag/panaderia?refer=embed">#panaderia</Link> 
              <Link title="cocina" target="_blank" href="https://www.tiktok.com/tag/cocina?refer=embed">#cocina</Link> 
              <Link title="amura" target="_blank" href="https://www.tiktok.com/tag/amura?refer=embed">#amura</Link> 
              <Link title="centroculinario" target="_blank" href="https://www.tiktok.com/tag/centroculinario?refer=embed">#centroculinario</Link> 
              <Link title="escueladecocina" target="_blank" href="https://www.tiktok.com/tag/escueladecocina?refer=embed">#escueladecocina</Link> 
              <Link title="competencia" target="_blank" href="https://www.tiktok.com/tag/competencia?refer=embed">#competencia</Link> 
              <Link title="reto" target="_blank" href="https://www.tiktok.com/tag/reto?refer=embed">#reto</Link> 
              <Link target="_blank" title="♬ Chichiquetere - HCTM" href="https://www.tiktok.com/music/Chichiquetere-7127855869164783617?refer=embed">♬ Chichiquetere - HCTM</Link> 
            </section> 
          </blockquote>

          <blockquote className="tiktok-embed" cite="https://www.tiktok.com/@amuracr/video/7268825160994671877" data-video-id="7268825160994671877" style={{maxWidth: "605px", minWidth: "325px"}}> 
            <section> 
              <Link target="_blank" title="@amuracr" href="https://www.tiktok.com/@amuracr?refer=embed">@amuracr</Link> Respuesta a @Mario Chacon4886 Aquí va 👌🏽 Comenten si quieren ver más 
              <Link title="escueladecocina" target="_blank" href="https://www.tiktok.com/tag/escueladecocina?refer=embed">#escueladecocina</Link> 
              <Link title="amuracentroculinario" target="_blank" href="https://www.tiktok.com/tag/amuracentroculinario?refer=embed">#amuracentroculinario</Link> 
              <Link title="gastronomia" target="_blank" href="https://www.tiktok.com/tag/gastronomia?refer=embed">#gastronomia</Link> 
              <Link title="chefenproceso" target="_blank" href="https://www.tiktok.com/tag/chefenproceso?refer=embed">#chefenproceso</Link> 
              <Link title="chef" target="_blank" href="https://www.tiktok.com/tag/chef?refer=embed">#chef</Link> 
              <Link target="_blank" title="♬ Say So (Instrumental Version) [Originally Performed by Doja Cat] - Elliot Van Coup" href="https://www.tiktok.com/music/Say-So-Instrumental-Version-6792987346171070465?refer=embed">♬ Say So (Instrumental Version) [Originally Performed by Doja Cat] - Elliot Van Coup</Link> 
            </section> 
          </blockquote>
          <blockquote className="tiktok-embed" cite="https://www.tiktok.com/@amuracr/video/7209108751087979781" data-video-id="7209108751087979781" style={{maxWidth: "605px", minWidth: "325px"}}> 
            <section> 
              <a target="_blank" title="@amuracr" href="https://www.tiktok.com/@amuracr?refer=embed">@amuracr</a> ¿Cuál fue tu técnica favorita de emplatado?🤔🤩 
              <a title="emplatado" target="_blank" href="https://www.tiktok.com/tag/emplatado?refer=embed">#emplatado</a> 
              <a title="tecnicas" target="_blank" href="https://www.tiktok.com/tag/tecnicas?refer=embed">#tecnicas</a> 
              <a title="amura" target="_blank" href="https://www.tiktok.com/tag/amura?refer=embed">#amura</a> 
              <a title="centroculinario" target="_blank" href="https://www.tiktok.com/tag/centroculinario?refer=embed">#centroculinario</a> 
              <a title="escueladecocina" target="_blank" href="https://www.tiktok.com/tag/escueladecocina?refer=embed">#escueladecocina</a> 
              <a title="gastronomia" target="_blank" href="https://www.tiktok.com/tag/gastronomia?refer=embed">#gastronomia</a> 
              <a title="estudiantes" target="_blank" href="https://www.tiktok.com/tag/estudiantes?refer=embed">#estudiantes</a> 
              <a title="cocina" target="_blank" href="https://www.tiktok.com/tag/cocina?refer=embed">#cocina</a> 
              <a title="fyp" target="_blank" href="https://www.tiktok.com/tag/fyp?refer=embed">#fyp</a> 
              <a target="_blank" title="♬ FEEL THE GROOVE - Queens Road, Fabian Graetz" href="https://www.tiktok.com/music/FEEL-THE-GROOVE-6769046027488987137?refer=embed">♬ FEEL THE GROOVE - Queens Road, Fabian Graetz</a> 
            </section> 
          </blockquote>
        </div>
        <div className="flex items-center justify-center gap-4 py-10">
          <Link href="https://www.instagram.com/amura.cr" target='_blank' aria-label="Instagram" className="rounded-full border border-gray-300 px-3 py-2 hover:shadow-lg hover:bg-border hover:border-orange cursor-pointer">
            <FontAwesomeIcon icon={faInstagram} className="text-orange font-thin" />
          </Link>
          <Link href="https://www.facebook.com/amura.cr" target='_blank' aria-label="Facebook"  className="rounded-full border border-gray-300 px-3 py-2 hover:shadow-lg hover:bg-border hover:border-orange cursor-pointer">
            <FontAwesomeIcon icon={faFacebook} className="text-orange font-thin" />
          </Link>
          <Link href="https://youtube.com/@amuracentroculinario1581" target='_blank' aria-label="YouTube" className="rounded-full border border-gray-300 px-3 py-2 hover:shadow-lg hover:bg-border hover:border-orange cursor-pointer">
            <FontAwesomeIcon icon={faYoutube} className="text-orange font-thin" />
          </Link>
        </div>
        <p className="text-center text-lg font-thin md:text-xl">¡Acompañanos en tu red favorita!</p>
      </div>
    </section>
  );
}
